import { Box, Button, Skeleton, Typography } from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { BriefItem, RevOpsBrief } from "Models/RevOpsBrief";
import { advisor, InfoHint } from "Components/AdvisorTheme";
import { AskAboutButton, BriefFeedbackBundle, useItemFeedback } from "Components/TodaysBrief";

type Kind = "risk" | "opportunity";
interface PriorityEntry { item: BriefItem; section: "risks_brief" | "opportunities_brief"; kind: Kind }

const MAX_PRIORITIES = 3;

/**
 * The brief does not store which pipeline its narrative used: the generator mixes
 * main-pipeline metrics with company-wide rep results. So these items are never
 * filtered by the pipeline selector and are labelled as company-level.
 */
export const BRIEF_SCOPE_LABEL = "Company brief · not filtered by pipeline";
export const BRIEF_SCOPE_DETAIL =
  "Priorities come from the daily brief generated once for your company. They can combine pipeline metrics with company-wide rep results " +
  "(wins, losses and time to close for deals closed in the last 30 days across all pipelines). The brief does not record which pipeline it used, " +
  "so the pipeline selector does not filter them and they may describe a different pipeline or period than the revenue snapshot.";

export function ScopeChip() {
  return (
    <Box component="span" sx={{ display: "inline-block", px: 1, py: 0.125, borderRadius: "6px", fontSize: 12, fontWeight: 600, bgcolor: advisor.lavender, color: advisor.muted, border: `1px solid ${advisor.lavenderBorder}`, whiteSpace: "nowrap" }}>
      {BRIEF_SCOPE_LABEL}
    </Box>
  );
}

/** Risks then opportunities, each in source order. Not an impact ranking. */
export const priorityEntries = (brief: RevOpsBrief): PriorityEntry[] => [
  ...(brief.risks_brief ?? []).map(item => ({ item, section: "risks_brief" as const, kind: "risk" as const })),
  ...(brief.opportunities_brief ?? []).map(item => ({ item, section: "opportunities_brief" as const, kind: "opportunity" as const })),
];

const cardSx = {
  bgcolor: advisor.card,
  border: `1px solid ${advisor.border}`,
  borderRadius: advisor.radius,
  boxShadow: advisor.shadow,
  p: { xs: 2, sm: 2.25 },
  minWidth: 0,
  display: "flex",
  flexDirection: "column" as const,
};

function PriorityCard({ entry, fb }: { entry: PriorityEntry; fb: BriefFeedbackBundle }) {
  const { item, section, kind } = entry;
  const { controls, details } = useItemFeedback(item, section, fb);
  const tone = kind === "risk" ? advisor.risk : advisor.opportunity;
  const Icon = kind === "risk" ? ReportProblemOutlinedIcon : TrendingUpRoundedIcon;
  return (
    <Box component="article" aria-label={`${kind === "risk" ? "Risk" : "Opportunity"}: ${item.category}`} sx={cardSx}>
      <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
        <Box aria-hidden sx={{ flexShrink: 0, width: 30, height: 30, borderRadius: "50%", bgcolor: tone.bg, color: tone.fg, display: "grid", placeItems: "center" }}>
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, color: advisor.text, lineHeight: 1.3, overflowWrap: "anywhere" }}>
            {item.category}
          </Typography>
          <Box component="span" sx={{ display: "inline-block", mt: 0.75, px: 1, py: 0.125, borderRadius: "6px", fontSize: 12, fontWeight: 700, bgcolor: tone.bg, color: tone.fg }}>
            {kind === "risk" ? "Risk" : "Opportunity"}
          </Box>
        </Box>
      </Box>
      <Typography sx={{ fontSize: 14.5, color: advisor.muted, lineHeight: 1.55, mt: 1.25, mb: 1.5, flex: 1, overflowWrap: "anywhere" }}>
        {item.text}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <AskAboutButton item={item} section={section} briefDate={fb.briefDate} />
        {controls}
      </Box>
      {details}
    </Box>
  );
}

const gridFor = (n: number) => ({
  display: "grid",
  gap: { xs: 1.5, md: 2 },
  gridTemplateColumns: {
    xs: "1fr",
    sm: n === 1 ? "1fr" : "repeat(2, minmax(0, 1fr))",
    md: `repeat(${Math.max(n, 1)}, minmax(0, 1fr))`,
  },
  ...(n === 3 ? { "& > :nth-of-type(3)": { gridColumn: { sm: "1 / -1", md: "auto" } } } : {}),
});

interface TopPrioritiesProps {
  brief: RevOpsBrief | null | undefined;
  loading: boolean;
  fb: BriefFeedbackBundle;
  onOpenBrief: () => void;
}

export default function TopPriorities({ brief, loading, fb, onOpenBrief }: TopPrioritiesProps) {
  const entries = brief ? priorityEntries(brief) : [];
  const shown = entries.slice(0, MAX_PRIORITIES);
  return (
    <Box component="section" aria-labelledby="priorities-title">
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.25, flexWrap: "wrap" }}>
        <Typography id="priorities-title" component="h2" sx={{ fontSize: 18, fontWeight: 700, color: advisor.text }}>
          Top Priorities for You
        </Typography>
        <InfoHint label="About these priorities">
          {BRIEF_SCOPE_DETAIL} Shown in brief order: risks first, then opportunities. The brief does not rank items by impact.
          Remaining items, What Changed and Recommendations are in the full brief.
        </InfoHint>
        <ScopeChip />
        {entries.length > shown.length && (
          <Button size="small" onClick={onOpenBrief} endIcon={<ArrowForwardRoundedIcon />} sx={{ ml: "auto", whiteSpace: "nowrap" }}>
            View all {entries.length}
          </Button>
        )}
      </Box>
      {loading ? (
        <Box sx={gridFor(MAX_PRIORITIES)}>
          {Array.from({ length: MAX_PRIORITIES }, (_, i) => (
            <Box key={i} sx={cardSx}><Skeleton width="50%" /><Skeleton width="30%" /><Skeleton height={60} /></Box>
          ))}
        </Box>
      ) : shown.length ? (
        <Box sx={gridFor(shown.length)}>
          {shown.map(entry => <PriorityCard key={`${entry.section}-${entry.item.id ?? entry.item.text}`} entry={entry} fb={fb} />)}
        </Box>
      ) : (
        <Box sx={{ ...cardSx, color: advisor.muted, fontSize: 15 }}>
          {brief ? "No risks or opportunities in this brief." : "Priorities will appear once a revenue brief is available."}
        </Box>
      )}
    </Box>
  );
}
